import { Fragment } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { listJourneys, type VisitorJourney } from "@/lib/journeys.functions";
import { unlockUploads } from "@/lib/uploads-gate.functions";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/uploads/journeys")({
  head: () => ({
    ...pageSeo({
      path: "/uploads/journeys",
      title: "Visitor Journeys | CRS Energy",
      description: "Internal dashboard for visitor page paths.",
    }),
    meta: [{ name: "robots", content: "noindex, nofollow" }],
  }),
  validateSearch: (s: Record<string, unknown>) => ({
    days: typeof s.days === "number" ? s.days : Number(s.days) || 30,
  }),
  loaderDeps: ({ search }) => ({ days: search.days }),
  loader: async ({ deps }) => listJourneys({ data: { days: deps.days } }),
  component: JourneysPage,
});

function formatDuration(startIso: string, endIso: string): string {
  const s = Math.max(0, Math.round((+new Date(endIso) - +new Date(startIso)) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function JourneysPage() {
  const data = Route.useLoaderData();
  const { days } = Route.useSearch();
  const router = useRouter();
  const unlock = useServerFn(unlockUploads);
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(false);
    try {
      const { ok } = await unlock({ data: { password } });
      if (ok) await router.invalidate();
      else setError(true);
    } finally {
      setSubmitting(false);
    }
  }

  if (!data.unlocked) {
    return (
      <section className="container-page max-w-md py-16 md:py-24">
        <h1 className="text-2xl font-bold mb-2">Visitor Journeys</h1>
        <p className="text-sm text-muted-foreground mb-6">
          Enter the password to view visitor paths.
        </p>
        <form onSubmit={onSubmit} className="space-y-4">
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
            placeholder="Password"
            autoComplete="current-password"
          />
          {error && <p className="text-sm text-destructive">Incorrect password.</p>}
          <button
            type="submit"
            disabled={submitting || !password}
            className="inline-flex items-center justify-center rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold shadow-cta hover:bg-accent/90 disabled:opacity-60"
          >
            {submitting ? "Checking..." : "Enter"}
          </button>
        </form>
      </section>
    );
  }

  const journeys = data.journeys as VisitorJourney[];

  return (
    <section className="container-page py-10 md:py-14">
      <header className="mb-6 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Visitor Journeys</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {journeys.length} unique visitors in the last {days} days. Click a row to see the full path.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Window:</span>
          {[1, 7, 30, 90].map((d) => (
            <Link
              key={d}
              to="/uploads/journeys"
              search={{ days: d }}
              className={
                "rounded-full px-3 py-1 border " +
                (days === d
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border hover:bg-muted")
              }
            >
              {d === 1 ? "24h" : `${d}d`}
            </Link>
          ))}
          <Link to="/uploads" className="ml-3 text-accent hover:underline">
            Uploads dashboard
          </Link>
        </div>
      </header>

      <div className="overflow-x-auto rounded-xl border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3 font-semibold">Last seen</th>
              <th className="p-3 font-semibold">Source</th>
              <th className="p-3 font-semibold">Landing page</th>
              <th className="p-3 font-semibold">Path length</th>
              <th className="p-3 font-semibold">Duration</th>
              <th className="p-3 font-semibold">Forms</th>
              <th className="p-3 font-semibold">Identity</th>
            </tr>
          </thead>
          <tbody>
            {journeys.map((j) => {
              const isOpen = !!expanded[j.visitor_id];
              return (
                <Fragment key={j.visitor_id}>
                  <tr
                    className="border-t align-top cursor-pointer hover:bg-muted/30"
                    onClick={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [j.visitor_id]: !prev[j.visitor_id],
                      }))
                    }
                  >
                    <td className="p-3 whitespace-nowrap text-muted-foreground">
                      {new Date(j.last_seen).toLocaleString("en-GB")}
                    </td>
                    <td className="p-3">
                      {j.sources.length ? (
                        j.sources.map((s) => (
                          <span
                            key={s}
                            className="inline-block rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs mr-1 mb-1"
                          >
                            {s}
                          </span>
                        ))
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs">{j.landing_page ?? "-"}</td>
                    <td className="p-3 whitespace-nowrap">
                      {j.pageviews} page{j.pageviews === 1 ? "" : "s"}
                      {j.distinct_days > 1 && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({j.distinct_days} days)
                        </span>
                      )}
                    </td>
                    <td className="p-3 whitespace-nowrap text-muted-foreground">
                      {formatDuration(j.first_seen, j.last_seen)}
                    </td>
                    <td className="p-3">
                      {j.form_events > 0 ? (
                        <span className="inline-block rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs">
                          {j.form_events}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      {j.name || j.email ? (
                        <div>
                          {j.name && <div className="font-medium">{j.name}</div>}
                          {j.email && (
                            <div className="text-xs text-muted-foreground">{j.email}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground font-mono">
                          {j.visitor_id.slice(0, 8)}
                        </span>
                      )}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={j.visitor_id + "-detail"} className="border-t bg-muted/20">
                      <td colSpan={7} className="p-4">
                        <div className="mb-2 text-xs text-muted-foreground">
                          Visitor ID: <span className="font-mono">{j.visitor_id}</span>
                          {j.referrers.length > 0 && (
                            <>
                              {" · "}Referrers:{" "}
                              <span className="font-mono">{j.referrers.join(", ")}</span>
                            </>
                          )}
                        </div>
                        <ol className="space-y-1">
                          {j.path.map((step, i) => (
                            <li
                              key={i}
                              className="flex items-baseline gap-3 text-xs font-mono"
                            >
                              <span className="text-muted-foreground w-40 shrink-0">
                                {new Date(step.visited_at).toLocaleString("en-GB")}
                              </span>
                              <span className="text-muted-foreground w-16 shrink-0">
                                {step.event_type ?? "pageview"}
                              </span>
                              <span className="text-foreground">{step.page}</span>
                            </li>
                          ))}
                        </ol>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {journeys.length === 0 && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-muted-foreground">
                  No visits in this window yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
